import { del, entries, set } from 'idb-keyval'
import { ApiError, NetworkError, api, uploadPhoto } from '../api/client.js'
import { t } from '../i18n/index.js'

const KEY_PREFIX = 'outbox:'

export interface OutboxPhoto {
  kind: 'before' | 'after' | 'other' | 'issue' | 'signature'
  blob: Blob
  takenAt: string
  /** Filled in once the photo reaches the server, so retries do not re-upload. */
  photoId?: string
}

export interface OutboxEntry {
  id: string
  kind: 'completion' | 'issue'
  taskId?: string
  objectId?: string
  title?: string
  clientRef: string
  completedAtClient: string
  note?: string
  /** The Textbaustein chosen for the completion, when the Leistung has any. */
  textOption?: string
  measurementValue?: number
  signedBy?: string
  /** Mangelmeldung, Legionellen. */
  defectNote?: string
  /** Only present when GPS is on and the person agreed. */
  location?: { lat: number; lng: number; accuracy: number }
  photos: OutboxPhoto[]
  createdAt: string
  attempts: number
  lastError?: string
}

export type OutboxListener = (state: OutboxState) => void

export interface OutboxState {
  pending: number
  syncing: boolean
  online: boolean
  lastError?: string
  lastSyncAt?: string
}

let listeners: OutboxListener[] = []
let state: OutboxState = { pending: 0, syncing: false, online: navigator.onLine }

function emit(patch: Partial<OutboxState>) {
  state = { ...state, ...patch }
  for (const listener of listeners) listener(state)
}

export function subscribeOutbox(listener: OutboxListener): () => void {
  listeners.push(listener)
  listener(state)
  return () => {
    listeners = listeners.filter((l) => l !== listener)
  }
}

export function getOutboxState(): OutboxState {
  return state
}

async function allEntries(): Promise<OutboxEntry[]> {
  const rows = await entries<string, OutboxEntry>()
  return rows
    .filter(([key]) => typeof key === 'string' && key.startsWith(KEY_PREFIX))
    .map(([, value]) => value)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

async function refreshCount(): Promise<number> {
  const pending = (await allEntries()).length
  emit({ pending })
  return pending
}

/**
 * Records a completion locally and returns immediately. The worker sees the
 * task as done at once; delivery is this module's problem, not theirs.
 */
export async function enqueue(entry: Omit<OutboxEntry, 'createdAt' | 'attempts'>): Promise<void> {
  const full: OutboxEntry = { ...entry, createdAt: new Date().toISOString(), attempts: 0 }
  await set(KEY_PREFIX + entry.id, full)
  await refreshCount()
  void sync()
}

export async function pendingTaskIds(): Promise<Set<string>> {
  const rows = await allEntries()
  return new Set(rows.filter((r) => r.taskId).map((r) => r.taskId!))
}

export async function listOutbox(): Promise<OutboxEntry[]> {
  return allEntries()
}

async function flushEntry(entry: OutboxEntry): Promise<void> {
  // Upload photos first, remembering each id, so a retry after a mid-way
  // failure resumes instead of starting over.
  for (const photo of entry.photos) {
    if (photo.photoId) continue
    const uploaded = await uploadPhoto(photo.blob, photo.kind, photo.takenAt)
    photo.photoId = uploaded.id
    await set(KEY_PREFIX + entry.id, entry)
  }

  const photoIds = entry.photos.map((p) => p.photoId!).filter(Boolean)

  if (entry.kind === 'completion') {
    await api(`/api/me/tasks/${entry.taskId}/complete`, {
      method: 'POST',
      body: {
        clientRef: entry.clientRef,
        completedAtClient: entry.completedAtClient,
        note: entry.note,
        textOption: entry.textOption,
        measurementValue: entry.measurementValue,
        signedBy: entry.signedBy,
        defectNote: entry.defectNote,
        location: entry.location,
        photoIds,
      },
    })
  } else {
    await api('/api/issues', {
      method: 'POST',
      body: {
        clientRef: entry.clientRef,
        objectId: entry.objectId,
        occurrenceId: entry.taskId ?? null,
        title: entry.title,
        description: entry.note,
        photoIds,
      },
    })
  }

  await del(KEY_PREFIX + entry.id)
}

let syncing = false

export async function sync(): Promise<void> {
  if (syncing) return
  if (!navigator.onLine) {
    emit({ online: false })
    return
  }

  syncing = true
  emit({ syncing: true, online: true })
  let lastError: string | undefined

  try {
    for (const entry of await allEntries()) {
      try {
        await flushEntry(entry)
      } catch (error) {
        if (error instanceof NetworkError) {
          // Still offline, stop, keep everything, try again later.
          lastError = undefined
          emit({ online: false })
          break
        }
        // A colleague in the group was faster. Nothing to retry, say who it was.
        if (error instanceof ApiError && error.code === 'already_done') {
          const by = typeof error.details?.by === 'string' ? error.details.by : null
          lastError = by ? t('task.already_done', { name: by }) : t('task.already_done_anon')
          await del(KEY_PREFIX + entry.id)
          continue
        }
        // A rejection from the server (task reassigned, already done elsewhere)
        // will not fix itself by retrying forever.
        entry.attempts += 1
        entry.lastError = error instanceof Error ? error.message : String(error)
        lastError = entry.lastError
        if (entry.attempts >= 5) {
          await del(KEY_PREFIX + entry.id)
        } else {
          await set(KEY_PREFIX + entry.id, entry)
        }
      }
    }
  } finally {
    syncing = false
    const pending = await refreshCount()
    emit({
      syncing: false,
      lastError,
      lastSyncAt: pending === 0 ? new Date().toISOString() : state.lastSyncAt,
    })
  }
}

/** Wires the queue to connectivity and visibility changes. Call once at startup. */
export function startOutbox(): () => void {
  const onOnline = () => {
    emit({ online: true })
    void sync()
  }
  const onOffline = () => emit({ online: false })
  const onVisible = () => {
    if (document.visibilityState === 'visible') void sync()
  }

  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  document.addEventListener('visibilitychange', onVisible)

  void refreshCount()
  void sync()
  const interval = setInterval(() => {
    if (state.pending > 0) void sync()
  }, 30_000)

  return () => {
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    document.removeEventListener('visibilitychange', onVisible)
    clearInterval(interval)
  }
}
